import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Stack,
  alpha,
  useTheme,
  Fade,
  Grow,
  Avatar
} from '@mui/material';
import {
  FileDownload as FileDownloadIcon,
  Description as ReportIcon,
  Business as CompanyIcon,
  School as StudentIcon,
  LocationCity as ITIIcon
} from '@mui/icons-material';
import { 
  getOJTTraineeReport,
  getApprenticeshipReport,
  getRecruitmentReport
} from '../Apis/IndustryConnectApis';
import { getApplications } from '../Apis/JobApis';
import { exportToCSV, exportToPDF } from '../Utils/ExportUtils';

const Reports = () => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);

  const handleExportExcel = () => {
    const reportNames = [
      'Student_Applications',
      'OJT_Trainees',
      'Apprenticeships',
      'Hiring_Analytics'
    ];
    
    let headers = [];
    let processedData = [];

    switch(tabValue) {
        case 0:
            headers = ['Company', 'Trade', 'Student', 'Status'];
            processedData = data.map(r => ({
                'Company': r.job?.company?.name || '—',
                'Trade': r.job?.title || '—',
                'Student': r.student?.studentName || '—',
                'Status': r.status
            }));
            break;
        case 1:
        case 2:
            headers = ['Trainee', 'Trade', 'Institute', 'Score', 'Follow-up Status', 'Status'];
            processedData = data.map(r => ({
                'Trainee': r.student?.studentName || '—',
                'Trade': r.job?.title || '—',
                'Institute': r.student?.college?.name || '—',
                'Score': r.student?.ojtEvaluations?.[0]?.totalMarks || 'N/A',
                'Follow-up Status': r.student?.industryFollowUp?.currentStatus || 'Not Tracked',
                'Status': r.status
            }));
            break;
        case 3:
            headers = ['ITI', 'Trade', 'Applications', 'Selected'];
            processedData = data.map(r => ({
                'ITI': r.iti,
                'Trade': r.trade,
                'Applications': r.applications,
                'Selected': r.selected
            }));
            break;
        default:
            break;
    }
    
    exportToCSV(processedData, headers, reportNames[tabValue]);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const fetchReportData = useCallback(async () => {
    setLoading(true);
    try {
      let response;
      switch (tabValue) {
        case 0: // Student Application Report
          response = await getApplications();
          const appData = response.data?.data || response.data || [];
          setData(Array.isArray(appData) ? appData : []);
          break;
        case 1: // OJT Trainee Report
          response = await getOJTTraineeReport();
          const ojtData = response.data || [];
          setData(Array.isArray(ojtData) ? ojtData : []);
          break;
        case 2: // Apprenticeship Trainee Report
          response = await getApprenticeshipReport();
          const appTraineeData = response.data || [];
          setData(Array.isArray(appTraineeData) ? appTraineeData : []);
          break;
        case 3: // Recruitment Report
          response = await getRecruitmentReport();
          const recruitData = response.data?.data || response.data || [];
          setData(Array.isArray(recruitData) ? recruitData : []);
          break;
        default:
          setData([]);
      }
    } catch (error) {
      console.error("Failed to fetch report:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [tabValue]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  const renderTable = () => {
    if (loading) return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 12, gap: 2 }}>
        <CircularProgress size={40} thickness={4} />
        <Typography variant="body2" color="textSecondary" fontWeight="bold">Assembling your report...</Typography>
      </Box>
    );

    if (data.length === 0) return (
      <Fade in={true}>
        <Box sx={{ py: 10, textAlign: 'center', bgcolor: alpha(theme.palette.background.paper, 0.5), borderRadius: 4, border: '1px dashed #CBD5E1' }}>
          <ReportIcon sx={{ fontSize: 48, color: '#94A3B8', mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" color="#64748B" fontWeight="bold">No records found</Typography>
          <Typography variant="body2" color="#94A3B8">Try switching tabs or adjusting filters.</Typography>
        </Box>
      </Fade>
    );

    return (
      <Grow in={!loading} timeout={400}>
        <TableContainer component={Paper} elevation={0} sx={{ 
          borderRadius: 4, 
          border: '1px solid #E2E8F0', 
          overflow: 'hidden',
          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)'
        }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {tabValue === 0 && (
                  <>
                    <TableCell sx={{ bgcolor: '#F8FAFC', fontWeight: '800' }}>COMPANY</TableCell>
                    <TableCell sx={{ bgcolor: '#F8FAFC', fontWeight: '800' }}>TRADE</TableCell>
                    <TableCell sx={{ bgcolor: '#F8FAFC', fontWeight: '800' }}>STUDENT</TableCell>
                    <TableCell sx={{ bgcolor: '#F8FAFC', fontWeight: '800' }}>STATUS</TableCell>
                  </>
                )}
                {(tabValue === 1 || tabValue === 2) && (
                  <>
                    <TableCell sx={{ bgcolor: '#F8FAFC', fontWeight: '800' }}>TRAINEE</TableCell>
                    <TableCell sx={{ bgcolor: '#F8FAFC', fontWeight: '800' }}>TRADE</TableCell>
                    <TableCell sx={{ bgcolor: '#F8FAFC', fontWeight: '800' }}>INSTITUTE</TableCell>
                    <TableCell sx={{ bgcolor: '#F8FAFC', fontWeight: '800' }}>EVALUATION</TableCell>
                    <TableCell sx={{ bgcolor: '#F8FAFC', fontWeight: '800' }}>FOLLOW-UP</TableCell>
                    <TableCell sx={{ bgcolor: '#F8FAFC', fontWeight: '800' }}>EVIDENCE</TableCell>
                    <TableCell sx={{ bgcolor: '#F8FAFC', fontWeight: '800' }}>STATUS</TableCell>
                  </>
                )}
                {tabValue === 3 && (
                  <>
                    <TableCell sx={{ bgcolor: '#F8FAFC', fontWeight: '800' }}>INSTITUTE / ITI</TableCell>
                    <TableCell sx={{ bgcolor: '#F8FAFC', fontWeight: '800' }}>TRADE</TableCell>
                    <TableCell sx={{ bgcolor: '#F8FAFC', fontWeight: '800', textAlign: 'center' }}>APPLICATIONS</TableCell>
                    <TableCell sx={{ bgcolor: '#F8FAFC', fontWeight: '800', textAlign: 'center' }}>SELECTED</TableCell>
                  </>
                )}

              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row, idx) => (
                <TableRow key={row.id || idx} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  {tabValue === 0 && (
                    <>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Avatar sx={{ width: 24, height: 24, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}><CompanyIcon sx={{ fontSize: 14 }} /></Avatar>
                          <Typography variant="body2" fontWeight="600">{row.job?.company?.name || '—'}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell><Typography variant="body2" color="textSecondary">{row.job?.title || '—'}</Typography></TableCell>
                      <TableCell><Typography variant="body2" fontWeight="bold">{row.student?.studentName || '—'}</Typography></TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          fontWeight="800" 
                          sx={{ 
                            fontSize: '0.8rem', 
                            color: (row.status === 'selected' || row.status === 'hired') ? theme.palette.success.main : '#F59E0B',
                            textTransform: 'uppercase'
                          }}
                        >
                          {row.status}
                        </Typography>
                      </TableCell>
                    </>
                  )}
                  {(tabValue === 1 || tabValue === 2) && (
                    <>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Avatar sx={{ width: 32, height: 32, bgcolor: '#F1F5F9', color: '#475569' }}><StudentIcon sx={{ fontSize: 18 }} /></Avatar>
                          <Typography variant="body2" fontWeight="700">{row.student?.studentName || '—'}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell><Typography variant="body2" color="textSecondary">{row.job?.title || '—'}</Typography></TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <ITIIcon sx={{ fontSize: 16, color: '#94A3B8' }} />
                          <Typography variant="body2">{row.student?.college?.name || '—'}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {row.student?.ojtEvaluations?.[0] ? (
                          <Box>
                            <Typography variant="body2" fontWeight="bold" color="primary">
                              {row.student.ojtEvaluations[0].totalMarks}/50
                            </Typography>
                          </Box>
                        ) : 'Pending'}
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          fontWeight="700" 
                          sx={{ 
                            fontSize: '0.8rem', 
                            color: row.student?.industryFollowUp?.currentStatus ? '#F59E0B' : '#94A3B8',
                            textTransform: 'uppercase'
                          }}
                        >
                          {row.student?.industryFollowUp?.currentStatus || 'Pending'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {row.student?.ojtMonitorings?.[0]?.evidenceUrl ? (
                          <Button 
                            variant="outlined" 
                            size="small"
                            href={row.student.ojtMonitorings[0].evidenceUrl} 
                            target="_blank" 
                            sx={{ fontSize: '0.8rem', fontWeight: '700', textTransform: 'none', px: 1, py: 0.2, borderRadius: 2 }}
                          >
                            View Document
                          </Button>
                        ) : (
                          <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#94A3B8' }}>No Evidence</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="800" sx={{ fontSize: '0.8rem', color: theme.palette.success.main, textTransform: 'uppercase' }}>
                          {row.status}
                        </Typography>
                      </TableCell>
                    </>
                  )}
                  {tabValue === 3 && (
                    <>
                      <TableCell><Typography variant="body2" fontWeight="700">{row.iti}</Typography></TableCell>
                      <TableCell><Typography variant="body2" color="textSecondary">{row.trade}</Typography></TableCell>
                      <TableCell align="center"><Typography variant="body2" fontWeight="800">{row.applications}</Typography></TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight="900" sx={{ fontSize: '0.8rem', color: theme.palette.success.main }}>
                          {row.selected}
                        </Typography>
                      </TableCell>
                    </>
                  )}

                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grow>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 5 }, bgcolor: '#F8FAFC', minHeight: '100vh' }}>
      <Fade in={true} timeout={600}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', mb: 5, gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight="1000" color="#0F172A" sx={{ letterSpacing: -1 }}>Industry Intelligence</Typography>
            <Typography variant="body2" color="#64748B" sx={{ mt: 0.5 }}>Comprehensive tracking of industrial engagement and outcomes.</Typography>
          </Box>
          <Stack direction="row" spacing={1.5}>
            <Button 
                startIcon={<FileDownloadIcon />} 
                variant="white" 
                onClick={handleExportExcel}
                sx={{ 
                    bgcolor: 'white', 
                    color: '#475569', 
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)', 
                    border: '1px solid #E2E8F0',
                    fontWeight: 'bold',
                    '&:hover': { bgcolor: '#F1F5F9' }
                }}
            >
                Spreadsheet
            </Button>
            <Button 
                startIcon={<FileDownloadIcon />} 
                variant="contained" 
                onClick={exportToPDF}
                sx={{ 
                    bgcolor: '#0F172A', 
                    color: 'white', 
                    fontWeight: 'bold',
                    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.2)',
                    '&:hover': { bgcolor: '#1E293B' }
                }}
            >
                Analysis PDF
            </Button>
          </Stack>
        </Box>
      </Fade>

      <Paper elevation={0} sx={{ 
        mb: 4, 
        borderRadius: 4, 
        p: 1, 
        bgcolor: 'white', 
        border: '1px solid #E2E8F0',
        display: 'inline-flex',
        maxWidth: '100%',
        overflowX: 'auto'
      }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTabs-indicator': { height: '100%', borderRadius: 3, bgcolor: alpha(theme.palette.primary.main, 0.08), zIndex: 0 },
            '& .MuiTab-root': { 
                minHeight: 44, 
                px: 3, 
                borderRadius: 3, 
                fontWeight: '800', 
                fontSize: '0.75rem', 
                color: '#64748B', 
                zIndex: 1,
                transition: 'all 0.2s',
                '&.Mui-selected': { color: theme.palette.primary.main }
            }
          }}
        >
          <Tab label="Applications" />
          <Tab label="OJT Monitoring" />
          <Tab label="Apprenticeships" />
          <Tab label="Hiring Analytics" />

        </Tabs>
      </Paper>

      {renderTable()}
    </Box>
  );
};

export default Reports;
